/**
 * IDEA CANVAS - Refine Tab v2.0
 *
 * Features:
 * - Selective refinement modes (full, headlines, visual, execution)
 * - Preview before save (BEFORE/AFTER comparison)
 * - Alternative headline navigation
 * - Format conversion
 * - Auto-generate image option
 *
 * @version 2.0
 */

import { GeneratorState } from '../../../features/generator/core/state.js';
import { apiRequest } from '../../api/client.js';
import { escapeHtml } from '../../core/sanitize.js';
// CREDIT-FIX: Import credit animation for visual feedback
import { animateCoinSpend, refreshCreditsFromServer } from '../credit-animation.js';
// REFACTOR-03: Import Events for centralized event emission
import { emit } from '../../core/events.js';
import { IDEA_EVENTS } from '../../core/event-types.js';
// Centralized pricing - Single Source of Truth (see frontend/js/core/pricing.js)
import { TEXT_COSTS, IMAGE_COSTS } from '../../core/pricing.js';

// Credit costs from centralized pricing
const REFINEMENT_COST = TEXT_COSTS.refinement;  // 3 credits
const FORMAT_CONVERSION_COST = TEXT_COSTS.format_conversion;  // 3 credits
const AUTO_IMAGE_COST = IMAGE_COSTS['seedream'];  // 5 credits (default provider)

// ============================================================================
// STATE
// ============================================================================

const refineState = {
  isRefining: false,
  currentMode: 'full',
  availableModes: [],
  previewData: null,      // Holds suggestions during preview
  selectedHeadlineIndex: 0,
  format: 'print',
  currentIdea: null,      // Current idea being refined
  selectedConvertFormat: null,  // Selected target format for conversion (null = none selected)
  headlineMergeMode: 'add'  // 'add' = append to existing, 'replace' = replace existing
};

// Expert labels mapping
const EXPERT_LABELS = {
  'full': 'Creative Director',
  'headlines': 'Copywriter',
  'visual': 'Art Director',
  'execution': 'Scene Director'
};

// Expert button text - uses REFINEMENT_COST from centralized pricing
const EXPERT_BUTTON_TEXT = {
  'full': `REFINE WITH CREATIVE DIRECTOR (${TEXT_COSTS.refinement} C)`,
  'headlines': `REFINE WITH COPYWRITER (${TEXT_COSTS.refinement} C)`,
  'visual': `REFINE WITH ART DIRECTOR (${TEXT_COSTS.refinement} C)`,
  'execution': `REFINE WITH SCENE DIRECTOR (${TEXT_COSTS.refinement} C)`
};

// Expert icons (pixel art character icons)
const EXPERT_ICONS = {
  'full': '/assets/icons/refine-idea-icon-01-creative-director.png',
  'headlines': '/assets/icons/refine-idea-icon-02-copywriter.png',
  'visual': '/assets/icons/refine-idea-icon-03-art-director.png',
  'execution': '/assets/icons/refine-idea-icon-04-scene-director.png'
};

// Expert colors for badges and selection borders
const EXPERT_COLORS = {
  'full': '#9966FF',      // Purple - Creative Director
  'headlines': '#0099FF',  // Blue - Copywriter
  'visual': '#FF6600',     // Orange - Art Director
  'execution': '#00CC66'   // Green - Scene Director
};

// Format icons for conversion
const FORMAT_ICONS = {
  video: '\u25B6',      // Play
  social: '\u25D0',     // Half circle
  digital: '\u25A3',    // Square with dot
  experiential: '\u2605', // Star
  campaign: '\u25C8',   // Diamond
  print: '\u25A0'       // Square
};

// ============================================================================
// INITIALIZE
// ============================================================================

/**
 * Initialize the refine tab for an idea
 * @param {Object} idea - The idea object
 */
export async function initRefineTab(idea) {
  refineState.format = (idea.format || 'print').toLowerCase();
  refineState.currentMode = 'full';
  refineState.previewData = null;
  refineState.selectedHeadlineIndex = 0;
  refineState.currentIdea = idea;
  refineState.selectedConvertFormat = null;

  // Fetch available modes for this format
  try {
    const data = await apiRequest(`/api/refinement-modes?format=${refineState.format}`);
    if (data.success) {
      refineState.availableModes = data.modes;
    } else {
      refineState.availableModes = getDefaultModes(refineState.format);
    }
  } catch (err) {
    console.warn('[RefineForm] Could not fetch modes, using defaults:', err.message);
    refineState.availableModes = getDefaultModes(refineState.format);
  }

  // Render the v2 UI
  const refineContainer = document.querySelector('.canvas-tab-content[data-tab="refine"]');
  if (refineContainer) {
    refineContainer.innerHTML = renderRefineTabReady(idea);
    console.log('[RefineForm] V2 UI rendered with modes:', refineState.availableModes.map(m => m.mode));
  }

  return refineState.availableModes;
}

/**
 * Get default modes if API fails
 */
function getDefaultModes(format) {
  const modes = [
    { mode: 'full', label: 'Full Refinement', description: 'Improve everything', expert: 'Creative Director' },
    { mode: 'headlines', label: 'Headlines Only', description: 'Improve headline/subline + alternatives', expert: 'Copywriter' }
  ];

  // For video format, skip "Visual Only" since scenes/execution covers it
  if (format !== 'video') {
    modes.push({
      mode: 'visual',
      label: 'Visual Only',
      description: 'Improve visual description',
      expert: 'Art Director'
    });
  }

  const executionFormats = ['video', 'social', 'digital', 'experiential', 'campaign'];
  if (executionFormats.includes(format)) {
    modes.push({
      mode: 'execution',
      label: format === 'video' ? 'Scenes Only' : 'Execution Only',
      description: 'Improve structure and flow',
      expert: 'Director'
    });
  }

  return modes;
}

// ============================================================================
// RENDER REFINE TAB - READY STATE
// ============================================================================

/**
 * Render the REFINE tab in ready state (mode selection)
 * @param {Object} idea - The idea object
 * @returns {string} HTML string
 */
export function renderRefineTabReady(idea) {
  const format = (idea.format || 'print').toLowerCase();
  const headline = idea.headline || idea.super || idea.hook || idea.cta || '';
  const visual = idea.visual || idea.text || '';
  const title = idea.title || 'Untitled';
  const isRefined = idea.isRefined || idea.is_refined || false;
  const hasAutoGen = GeneratorState?.imageConfig?.autoGeneration;
  const visualFormats = ['print', 'social', 'digital', 'experiential'];
  const showImageToggle = hasAutoGen && visualFormats.includes(format);

  // Build mode options with character icons
  const modeOptionsHtml = refineState.availableModes.map(m => {
    const isSelected = m.mode === refineState.currentMode;
    const expertColor = EXPERT_COLORS[m.mode] || '#000000';
    const expertIcon = EXPERT_ICONS[m.mode] || '';
    const expertLabel = EXPERT_LABELS[m.mode] || m.expert || '';

    return `
    <label class="refine-mode-card ${isSelected ? 'refine-mode-card--selected' : ''}"
           style="--expert-color: ${expertColor}"
           data-mode="${m.mode}">
      <input type="radio" name="refine-mode" value="${m.mode}" ${isSelected ? 'checked' : ''}>
      <div class="refine-mode-card__radio"></div>
      <div class="refine-mode-card__icon">
        <img src="${expertIcon}" alt="${expertLabel}" class="refine-mode-card__icon-img">
      </div>
      <div class="refine-mode-card__content">
        <span class="refine-mode-card__title">${m.label}</span>
        <span class="refine-mode-card__desc">${m.description}</span>
      </div>
      <span class="refine-mode-card__badge" style="background: ${expertColor}">${expertLabel.toUpperCase()}</span>
    </label>
  `;
  }).join('');

  // Build format conversion buttons (exclude current format)
  const allFormats = ['print', 'video', 'social', 'digital', 'experiential', 'campaign'];
  const selectedConvert = refineState.selectedConvertFormat;
  const convertButtonsHtml = allFormats
    .filter(f => f !== format)
    .map(f => `
      <button class="format-convert-btn ${selectedConvert === f ? 'format-convert-btn--selected' : ''}" data-format="${f}" title="Select ${f} format">
        <span class="format-icon">${FORMAT_ICONS[f]}</span>
        <span class="format-name">${f.toUpperCase()}</span>
      </button>
    `).join('');

  // Build confirmation section (only shown when a format is selected)
  const showImageCheckbox = visualFormats.includes(selectedConvert);
  const confirmationHtml = selectedConvert ? `
    <div class="convert-confirmation">
      <div class="convert-confirmation-header">
        <span class="convert-from">${format.toUpperCase()}</span>
        <span class="convert-arrow-icon">\u2192</span>
        <span class="convert-to">${selectedConvert.toUpperCase()}</span>
      </div>
      <p class="convert-confirmation-text">This will create a <strong>new ${selectedConvert.toUpperCase()} idea card</strong> based on your current concept. The original idea stays unchanged.</p>
      ${showImageCheckbox ? `
        <label class="refine-checkbox-label" style="margin: 12px 0;">
          <input type="checkbox" id="convert-generate-image" checked>
          <span class="checkbox-text">Generate image for new idea</span>
        </label>
      ` : ''}
      <div class="convert-confirmation-actions">
        <button class="refine-btn refine-btn-secondary" id="convert-btn-cancel">CANCEL</button>
        <button class="refine-btn refine-btn-primary" id="convert-btn-start">
          <span class="btn-text">CONVERT TO ${selectedConvert.toUpperCase()}</span>
          <span class="btn-loading" style="display:none;">CONVERTING...</span>
        </button>
      </div>
    </div>
  ` : '';

  return `
    <div class="refine-tab refine-tab--ready">
      <!-- Info tip -->
      <div class="prompt-tip-blue">
        <span class="tip-icon-blue">i</span>
        <span class="tip-text-blue">Select what to refine. Preview changes before saving.</span>
      </div>

      <!-- Current idea preview -->
      <div class="refine-section">
        <label class="refine-section-label">CURRENT IDEA</label>
        <div class="refine-current-preview">
          <div class="refine-preview-title">${escapeHtml(title)}</div>
          ${headline ? `<div class="refine-preview-headline">"${escapeHtml(headline)}"</div>` : ''}
          ${visual ? `<div class="refine-preview-visual">${escapeHtml(visual.substring(0, 150))}${visual.length > 150 ? '...' : ''}</div>` : ''}
        </div>
      </div>

      <!-- Mode selection -->
      <div class="refine-section">
        <label class="refine-section-label">WHAT TO REFINE</label>
        <div class="refine-mode-group" id="refine-mode-group">
          ${modeOptionsHtml}
        </div>
      </div>

      <!-- User guidance -->
      <div class="refine-section">
        <label class="refine-section-label">YOUR DIRECTION <span class="refine-optional">(OPTIONAL)</span></label>
        <textarea class="refine-guidance-textarea" id="canvas-refine-guidance" rows="3" spellcheck="false"
          placeholder="e.g., Make the headline punchier, focus on humor..."></textarea>
      </div>

      <!-- Auto-generate image toggle -->
      ${showImageToggle ? `
        <div class="refine-section refine-checkbox-section" id="canvas-refine-image-toggle">
          <label class="refine-checkbox-label">
            <input type="checkbox" id="canvas-refine-auto-image" checked>
            <span class="checkbox-text">Auto-generate image when visual changes</span>
          </label>
        </div>
      ` : ''}

      <!-- Refine button -->
      <div class="refine-actions">
        <button class="refine-btn refine-btn-primary" id="canvas-btn-refine-idea">
          <span class="btn-text">${EXPERT_BUTTON_TEXT[refineState.currentMode]}</span>
          <span class="btn-loading" style="display:none;">WORKING...</span>
        </button>
      </div>

      <!-- Refinement history note -->
      ${isRefined ? `
        <div class="refine-history-note">
          <span class="refine-badge">REFINED</span>
          This idea has been refined ${idea.refinementCount || 1} time(s).
        </div>
      ` : ''}

      <!-- Format conversion section -->
      <div class="refine-section refine-convert-section">
        <div class="refine-convert-divider"></div>
        <label class="refine-section-label">CHANGE FORMAT</label>
        <p class="refine-convert-desc">${selectedConvert ? 'Select a different format or confirm conversion:' : 'Click a format to convert this idea:'}</p>
        <div class="format-convert-grid">
          ${convertButtonsHtml}
        </div>
        ${confirmationHtml}
      </div>
    </div>
  `;
}

// ============================================================================
// RENDER REFINE TAB - PREVIEW STATE
// ============================================================================

/**
 * Render the REFINE tab in preview state (showing BEFORE/AFTER)
 * @param {Object} originalIdea - The original idea
 * @param {Object} previewData - The preview data from API
 * @returns {string} HTML string
 */
export function renderRefineTabPreview(originalIdea, previewData) {
  const { suggestions, original, critique, expertLabel, visualChanged } = previewData;
  // Use mode from previewData, fallback to refineState.currentMode
  const mode = previewData.mode || refineState.currentMode || 'full';
  const format = originalIdea.format || 'print';

  // Get expert color for this mode
  const expertColor = EXPERT_COLORS[mode] || '#FFFF00';
  // Generate softer version for backgrounds (add transparency)
  const expertColorSoft = expertColor + '20'; // 12% opacity

  console.log('[RefineForm] Rendering preview with mode:', mode, 'expertColor:', expertColor, 'visualChanged:', visualChanged);
  const alternativeHeadlines = suggestions.alternativeHeadlines ||
    mapAlternatives(suggestions, format);
  const totalHeadlines = alternativeHeadlines.length + 1;
  const hasAlternatives = alternativeHeadlines.length > 0;
  const visualFormats = ['print', 'social', 'digital', 'experiential'];
  const showImageToggle = visualFormats.includes(format);

  // Get headline field based on format
  const headlineField = getHeadlineField(format);
  const originalHeadline = original[headlineField] || original.headline || '';
  const newHeadline = suggestions[headlineField] || suggestions.headline || '';

  // Build comparison sections based on mode
  let comparisonHtml = '';

  // TITLE comparison (only for full mode - Creative Director)
  if (mode === 'full') {
    const originalTitle = original.title || '';
    const newTitle = suggestions.title || '';

    if (newTitle && newTitle !== originalTitle) {
      comparisonHtml += `
        <div class="refine-comparison">
          <div class="refine-comparison-label">TITLE</div>
          <div class="refine-before">
            <span class="refine-tag">BEFORE</span>
            <span class="refine-value">${escapeHtml(originalTitle || '(not set)')}</span>
          </div>
          <div class="refine-after">
            <span class="refine-tag">AFTER</span>
            <span class="refine-value">${escapeHtml(newTitle)}</span>
          </div>
        </div>
      `;
    }
  }

  // THE IDEA comparison (only for full mode - Creative Director)
  if (mode === 'full') {
    const originalIdea = original.idea || '';
    const newIdea = suggestions.idea || '';

    if (newIdea && newIdea !== originalIdea) {
      comparisonHtml += `
        <div class="refine-comparison">
          <div class="refine-comparison-label">THE IDEA</div>
          <div class="refine-before">
            <span class="refine-tag">BEFORE</span>
            <span class="refine-value">${escapeHtml(originalIdea || '(not set)')}</span>
          </div>
          <div class="refine-after">
            <span class="refine-tag">AFTER</span>
            <span class="refine-value">${escapeHtml(newIdea)}</span>
          </div>
        </div>
      `;
    }
  }

  if (mode === 'full' || mode === 'headlines') {
    // Build all headlines array: main headline + alternatives
    const allHeadlines = [newHeadline];
    alternativeHeadlines.forEach(alt => {
      allHeadlines.push(alt.headline || alt);
    });

    comparisonHtml += `
      <div class="refine-comparison">
        <div class="refine-comparison-label">
          ${getHeadlineLabel(format)}
        </div>
        <div class="refine-before">
          <span class="refine-tag">BEFORE</span>
          <span class="refine-value">"${escapeHtml(originalHeadline)}"</span>
        </div>
      </div>
    `;

    // Show all headlines in one selectable block
    comparisonHtml += `
      <div class="refine-headlines-block">
        <div class="refine-headlines-header" style="background: ${expertColor};">
          <span class="refine-headlines-after-tag">AFTER</span>
          <span class="refine-headlines-title">${getHeadlineLabel(format)} OPTIONS (${allHeadlines.length})</span>
          <div class="refine-headlines-nav">
            <button class="refine-alt-btn refine-alt-prev" id="preview-headline-prev">&lt;</button>
            <button class="refine-alt-btn refine-alt-next" id="preview-headline-next">&gt;</button>
          </div>
        </div>
        <div class="refine-headlines-list" id="preview-alt-list">
          ${allHeadlines.map((headline, i) => `
            <div class="refine-headline-item ${i === refineState.selectedHeadlineIndex ? 'refine-headline-item--selected' : ''}"
                 data-index="${i}">
              <span class="refine-headline-num">${i + 1}</span>
              <span class="refine-headline-text">"${escapeHtml(headline)}"</span>
            </div>
          `).join('')}
        </div>
        <!-- Headline merge mode toggle -->
        <div class="refine-headline-mode">
          <label class="refine-radio-label">
            <input type="radio" name="headline-merge-mode" value="add" ${refineState.headlineMergeMode === 'add' ? 'checked' : ''}>
            <span class="radio-text">Add to existing headlines</span>
          </label>
          <label class="refine-radio-label">
            <input type="radio" name="headline-merge-mode" value="replace" ${refineState.headlineMergeMode === 'replace' ? 'checked' : ''}>
            <span class="radio-text">Replace all headlines</span>
          </label>
        </div>
      </div>
    `;
  }

  if (mode === 'full' || mode === 'visual') {
    // Get format-specific visual field (social=sequence, digital=frames, etc.)
    const visualField = getVisualField(format);
    const visualLabel = getVisualLabel(format);

    // Try format-specific field first, then fallback to generic visual/text
    const originalVisual = original[visualField] || original.visual || original.text || '';
    const newVisual = suggestions[visualField] || suggestions.visual || suggestions.text || '';

    if (originalVisual || newVisual) {
      comparisonHtml += `
        <div class="refine-comparison">
          <div class="refine-comparison-label">${visualLabel}</div>
          <div class="refine-before">
            <span class="refine-tag">BEFORE</span>
            <span class="refine-value">${escapeHtml(originalVisual)}</span>
          </div>
          <div class="refine-after">
            <span class="refine-tag">AFTER</span>
            <span class="refine-value">${escapeHtml(newVisual)}</span>
          </div>
        </div>
      `;
    }
  }

  // Execution/Scenes mode (video format)
  if (mode === 'full' || mode === 'execution') {
    const originalScenes = original.scenes || [];
    const newScenes = suggestions.scenes || [];

    if (newScenes.length > 0) {
      comparisonHtml += `
        <div class="refine-comparison refine-comparison--scenes">
          <div class="refine-comparison-label">SCENES (${newScenes.length})</div>
          <div class="refine-scenes-list">
            ${newScenes.map((scene, i) => {
              const origScene = originalScenes[i] || {};
              const sceneNum = scene.seq || scene.scene || (i + 1);
              return `
                <div class="refine-scene-item">
                  <div class="refine-scene-header">SCENE ${sceneNum}</div>
                  <div class="refine-scene-row">
                    <div class="refine-scene-before">
                      <span class="refine-tag">BEFORE</span>
                      <span class="refine-scene-visual">${escapeHtml(origScene.visual || origScene.description || '(no original)')}</span>
                    </div>
                    <div class="refine-scene-after">
                      <span class="refine-tag">AFTER</span>
                      <span class="refine-scene-visual">${escapeHtml(scene.visual || scene.description || '')}</span>
                    </div>
                  </div>
                  ${scene.camera ? `<div class="refine-scene-meta"><strong>Camera:</strong> ${escapeHtml(scene.camera)}</div>` : ''}
                  ${scene.voiceover ? `<div class="refine-scene-meta"><strong>Voiceover:</strong> ${escapeHtml(scene.voiceover)}</div>` : ''}
                  ${scene.audio ? `<div class="refine-scene-meta"><strong>Audio:</strong> ${escapeHtml(scene.audio)}</div>` : ''}
                </div>
              `;
            }).join('')}
          </div>
        </div>
      `;
    }

    // Also show super/tagline for video if changed
    const originalSuper = original.super || '';
    const newSuper = suggestions.super || '';
    if (newSuper && newSuper !== originalSuper) {
      comparisonHtml += `
        <div class="refine-comparison">
          <div class="refine-comparison-label">SUPER</div>
          <div class="refine-before">
            <span class="refine-tag">BEFORE</span>
            <span class="refine-value">"${escapeHtml(originalSuper || '(none)')}"</span>
          </div>
          <div class="refine-after">
            <span class="refine-tag">AFTER</span>
            <span class="refine-value">"${escapeHtml(newSuper)}"</span>
          </div>
        </div>
      `;
    }

    // Format-specific execution rendering (non-video formats)
    if (format === 'social') {
      comparisonHtml += renderSocialExecution(original, suggestions);
    } else if (format === 'digital') {
      comparisonHtml += renderDigitalExecution(original, suggestions);
    } else if (format === 'experiential') {
      comparisonHtml += renderExperientialExecution(original, suggestions);
    } else if (format === 'campaign') {
      comparisonHtml += renderCampaignExecution(original, suggestions);
    }
  }

  // Critique section
  comparisonHtml += `
    <div class="refine-critique-section">
      <div class="refine-critique-label">WHAT WAS IMPROVED</div>
      <div class="refine-critique-text">"${escapeHtml(critique)}"</div>
    </div>
  `;

  return `
    <div class="refine-tab refine-tab--preview" style="--expert-color: ${expertColor}; --expert-color-soft: ${expertColorSoft};">
      <!-- Header -->
      <div class="refine-preview-header" style="background: ${expertColor};">
        <span class="refine-preview-title">REFINEMENT PREVIEW</span>
        <span class="refine-expert-badge">${expertLabel}</span>
      </div>

      <!-- Comparisons -->
      <div class="refine-preview-body">
        ${comparisonHtml}
      </div>

      <!-- Image toggle - only show when mode includes visual changes -->
      ${showImageToggle && (mode === 'full' || mode === 'visual') ? `
        <div class="refine-section refine-checkbox-section">
          <label class="refine-checkbox-label">
            <input type="checkbox" id="preview-generate-image" ${visualChanged ? 'checked' : ''}>
            <span class="checkbox-text">Generate new image with refined visual</span>
          </label>
        </div>
      ` : ''}

      <!-- Action buttons -->
      <div class="refine-preview-actions">
        <button class="refine-btn refine-btn-secondary" id="preview-btn-discard">
          DISCARD
        </button>
        <button class="refine-btn refine-btn-primary" id="preview-btn-accept">
          ACCEPT & SAVE
        </button>
      </div>
    </div>
  `;
}

// ============================================================================
// RENDER FORMAT CONVERSION PREVIEW
// ============================================================================

/**
 * Render format conversion preview
 * @param {Object} originalIdea - The original idea
 * @param {Object} convertedData - The converted idea data
 * @returns {string} HTML string
 */
export function renderConversionPreview(originalIdea, convertedData) {
  const { convertedIdea, originalFormat, newFormat, critique } = convertedData;
  const visualFormats = ['print', 'social', 'digital', 'experiential'];
  const showImageToggle = visualFormats.includes(newFormat);

  // Get visual descriptions
  const originalVisual = originalIdea.visual || originalIdea.text || '';
  const convertedVisual = convertedIdea.visual || convertedIdea.text || '';
  const originalHeadline = originalIdea.headline || originalIdea.super || originalIdea.hook || originalIdea.cta || '';

  // Build original summary
  const originalSummary = `
    <div class="convert-original">
      <div class="convert-format-badge">${originalFormat.toUpperCase()}</div>
      <div class="convert-summary">
        <div class="convert-title">${escapeHtml(originalIdea.title || 'Untitled')}</div>
        <div class="convert-headline">"${escapeHtml(originalHeadline)}"</div>
        ${originalVisual ? `<div class="convert-visual">${escapeHtml(originalVisual.substring(0, 150))}${originalVisual.length > 150 ? '...' : ''}</div>` : ''}
      </div>
    </div>
  `;

  // Build converted summary based on format
  let convertedSummary = `
    <div class="convert-converted">
      <div class="convert-format-badge convert-format-badge--new">${newFormat.toUpperCase()}</div>
      <div class="convert-summary">
        <div class="convert-title">${escapeHtml(convertedIdea.title || 'Untitled')}</div>
  `;

  if (newFormat === 'video' && convertedIdea.scenes) {
    convertedSummary += `
      <div class="convert-scenes">
        ${convertedIdea.scenes.slice(0, 3).map(s => `
          <div class="convert-scene">
            <span class="scene-num">Scene ${s.seq}</span>
            <span class="scene-visual">${escapeHtml((s.visual || '').substring(0, 80))}...</span>
          </div>
        `).join('')}
      </div>
      <div class="convert-super">"${escapeHtml(convertedIdea.super || '')}"</div>
    `;
  } else {
    const headline = convertedIdea.headline || convertedIdea.super || convertedIdea.hook || convertedIdea.cta || '';
    convertedSummary += `<div class="convert-headline">"${escapeHtml(headline)}"</div>`;
    if (convertedVisual) {
      convertedSummary += `<div class="convert-visual">${escapeHtml(convertedVisual.substring(0, 150))}${convertedVisual.length > 150 ? '...' : ''}</div>`;
    }
  }

  convertedSummary += `</div></div>`;

  return `
    <div class="refine-tab refine-tab--convert-preview">
      <!-- Header -->
      <div class="refine-preview-header">
        <span class="refine-preview-title">FORMAT CONVERSION</span>
        <span class="refine-convert-arrow">${originalFormat.toUpperCase()} \u2192 ${newFormat.toUpperCase()}</span>
      </div>

      <!-- Original vs Converted -->
      <div class="convert-comparison">
        ${originalSummary}
        <div class="convert-arrow">\u25BC</div>
        ${convertedSummary}
      </div>

      <!-- Critique -->
      <div class="refine-critique-section">
        <div class="refine-critique-label">HOW IT WAS ADAPTED</div>
        <div class="refine-critique-text">"${escapeHtml(critique)}"</div>
      </div>

      <!-- Info about new idea -->
      <div class="prompt-tip-blue" style="margin: 16px 0;">
        <span class="tip-icon-blue">i</span>
        <span class="tip-text-blue">This will create a new idea card. The original idea stays unchanged.</span>
      </div>

      <!-- Image toggle -->
      ${showImageToggle ? `
        <div class="refine-section refine-checkbox-section">
          <label class="refine-checkbox-label">
            <input type="checkbox" id="convert-generate-image" checked>
            <span class="checkbox-text">Generate image for new idea</span>
          </label>
        </div>
      ` : ''}

      <!-- Action buttons -->
      <div class="refine-preview-actions">
        <button class="refine-btn refine-btn-secondary" id="convert-btn-discard">
          CANCEL
        </button>
        <button class="refine-btn refine-btn-primary" id="convert-btn-accept">
          <span class="btn-text">CREATE ${newFormat.toUpperCase()} IDEA</span>
          <span class="btn-loading" style="display:none;">CREATING...</span>
        </button>
      </div>
    </div>
  `;
}

// ============================================================================
// EVENT HANDLERS
// ============================================================================

/**
 * Handle mode selection change
 * @param {string} mode - The selected mode
 */
export function handleModeChange(mode) {
  refineState.currentMode = mode;
  console.log('[RefineForm] Mode changed to:', mode);

  // Update UI - mode selection (support both old and new card design)
  const modeGroup = document.getElementById('refine-mode-group');
  if (modeGroup) {
    // New card design
    modeGroup.querySelectorAll('.refine-mode-card').forEach(card => {
      const input = card.querySelector('input');
      const isSelected = input?.value === mode;
      card.classList.toggle('refine-mode-card--selected', isSelected);
      if (input) input.checked = isSelected;
    });
    // Legacy support
    modeGroup.querySelectorAll('.refine-mode-option').forEach(opt => {
      const input = opt.querySelector('input');
      const isSelected = input?.value === mode;
      opt.classList.toggle('refine-mode-option--selected', isSelected);
      if (input) input.checked = isSelected;
    });
  }

  // Update button text
  const btn = document.getElementById('canvas-btn-refine-idea');
  if (btn) {
    const btnText = btn.querySelector('.btn-text');
    if (btnText) {
      btnText.textContent = EXPERT_BUTTON_TEXT[mode] || 'REFINE';
    }
  }

  // Show/hide image toggle based on mode
  // Only show for modes that affect visuals (full, visual)
  const imageToggle = document.getElementById('canvas-refine-image-toggle');
  if (imageToggle) {
    const showToggle = mode === 'full' || mode === 'visual';
    imageToggle.style.display = showToggle ? '' : 'none';
  }
}

/**
 * Handle format selection for conversion (toggle selection, don't trigger API)
 * @param {string} targetFormat - The format to select
 * @param {Object} currentIdea - The current idea
 */
export function handleSelectConvertFormat(targetFormat, currentIdea) {
  // Toggle selection: if clicking same format, deselect
  if (refineState.selectedConvertFormat === targetFormat) {
    refineState.selectedConvertFormat = null;
  } else {
    refineState.selectedConvertFormat = targetFormat;
  }

  console.log('[RefineForm] Format selection:', refineState.selectedConvertFormat);

  // Re-render the refine tab to show updated state
  const refineContainer = document.querySelector('.canvas-tab-content[data-tab="refine"]');
  if (refineContainer && currentIdea) {
    refineContainer.innerHTML = renderRefineTabReady(currentIdea);
  }
}

/**
 * Handle cancel format selection (clear selection, stay on page)
 * @param {Object} currentIdea - The current idea
 */
export function handleCancelConvertFormat(currentIdea) {
  refineState.selectedConvertFormat = null;
  console.log('[RefineForm] Format selection cancelled');

  // Re-render the refine tab
  const refineContainer = document.querySelector('.canvas-tab-content[data-tab="refine"]');
  if (refineContainer && currentIdea) {
    refineContainer.innerHTML = renderRefineTabReady(currentIdea);
  }
}

/**
 * Handle start conversion (user confirmed, now call API)
 * @param {Object} currentIdea - The current idea
 * @param {Object} callbacks - Callback functions
 */
export async function handleStartConversion(currentIdea, callbacks = {}) {
  const targetFormat = refineState.selectedConvertFormat;
  if (!targetFormat) {
    console.warn('[RefineForm] No format selected for conversion');
    return;
  }

  // Show loading state on button
  const btn = document.getElementById('convert-btn-start');
  if (btn) {
    btn.disabled = true;
    const btnText = btn.querySelector('.btn-text');
    const btnLoading = btn.querySelector('.btn-loading');
    if (btnText) btnText.style.display = 'none';
    if (btnLoading) btnLoading.style.display = 'inline';
  }

  // Call the existing handleConvertFormat which does the API call
  await handleConvertFormat(currentIdea, targetFormat, {
    ...callbacks,
    onError: (err) => {
      // Reset button on error
      if (btn) {
        btn.disabled = false;
        const btnText = btn.querySelector('.btn-text');
        const btnLoading = btn.querySelector('.btn-loading');
        if (btnText) btnText.style.display = 'inline';
        if (btnLoading) btnLoading.style.display = 'none';
      }
      if (callbacks.onError) callbacks.onError(err);
    }
  });
}

/**
 * Handle refinement request (generate preview)
 * @param {Object} currentIdea - The current idea
 * @param {Object} callbacks - Callback functions
 */
export async function handleRefine(currentIdea, callbacks = {}) {
  console.log('[RefineForm] handleRefine called with:', {
    currentIdea: currentIdea?.title,
    isRefining: refineState.isRefining,
    currentMode: refineState.currentMode
  });

  if (!currentIdea || refineState.isRefining) {
    console.log('[RefineForm] Early return - no idea or already refining');
    return;
  }

  const guidance = document.getElementById('canvas-refine-guidance')?.value?.trim() || '';
  const mode = refineState.currentMode;

  console.log('[RefineForm] Starting refinement with mode:', mode);

  // Update button state
  const submitBtn = document.getElementById('canvas-btn-refine-idea');
  const btnText = submitBtn?.querySelector('.btn-text');
  const btnLoading = submitBtn?.querySelector('.btn-loading');

  if (btnText) btnText.style.display = 'none';
  if (btnLoading) btnLoading.style.display = 'inline';
  if (submitBtn) submitBtn.disabled = true;

  refineState.isRefining = true;

  // CREDIT-FIX: Animate credit deduction for refinement (using centralized pricing)
  // Animation happens on submit - backend deducts atomically
  animateCoinSpend(REFINEMENT_COST);

  try {
    // Build brief with productData from session product (FIX: Include product context)
    const brief = {
      ...(GeneratorState?.brief || {}),
      brand: GeneratorState?.currentBrand || GeneratorState?.sessionBrand || null,
      productData: GeneratorState?.sessionProduct || null
    };

    // Call API to get suggestions (preview mode)
    const response = await apiRequest('/api/refine-idea', {
      method: 'POST',
      body: JSON.stringify({
        idea: currentIdea,
        brief,
        format: currentIdea.format || 'print',
        mode,
        guidance
      })
    });

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Refinement failed');
    }

    console.log('[RefineForm] Got preview data:', data.expertLabel, 'mode:', mode);

    // Store preview data with mode included
    refineState.previewData = { ...data, mode };
    refineState.selectedHeadlineIndex = 0;

    // Callback to show preview UI with mode included
    if (callbacks.onPreview) {
      callbacks.onPreview({ ...data, mode });
    }

  } catch (error) {
    console.error('[RefineForm] Refinement error:', error);
    if (window.Toast) window.Toast.error('Failed to refine: ' + error.message);
    // CREDIT-FIX: Rollback optimistic animation by refreshing true balance
    refreshCreditsFromServer();
  } finally {
    // Reset button state
    if (btnText) btnText.style.display = 'inline';
    if (btnLoading) btnLoading.style.display = 'none';
    if (submitBtn) submitBtn.disabled = false;
    refineState.isRefining = false;
  }
}

/**
 * Handle accepting refinement (apply changes)
 * @param {Object} currentIdea - The current idea
 * @param {Object} callbacks - Callback functions
 */
export async function handleAcceptRefinement(currentIdea, callbacks = {}) {
  if (!refineState.previewData) return;

  const generateImage = document.getElementById('preview-generate-image')?.checked ?? false;
  // Get headline merge mode from radio buttons (add = append to existing, replace = replace all)
  const headlineMergeMode = document.querySelector('input[name="headline-merge-mode"]:checked')?.value || refineState.headlineMergeMode;

  // CREDIT-FIX: Animate credit deduction if generating new image (using centralized pricing)
  if (generateImage) {
    animateCoinSpend(AUTO_IMAGE_COST);
  }

  try {
    // Call apply endpoint
    // Extract brandId and productId from idea or global state for proper context preservation
    const brandId = currentIdea.brandId || currentIdea.brand_id ||
                    currentIdea.meta?.brandId || GeneratorState?.currentBrand?.id || null;
    const productId = currentIdea.productId || currentIdea.product_id ||
                      currentIdea.meta?.brief?.productId || GeneratorState?.brief?.productId || null;

    // Also preserve the full productData and brief for meta storage
    const productData = currentIdea.meta?.brief?.productData ||
                        GeneratorState?.sessionProduct ||
                        GeneratorState?.selectedProduct || null;
    const brief = {
      ...(currentIdea.meta?.brief || {}),
      ...(GeneratorState?.brief || {}),
      productId,
      productData
    };

    console.log('[RefineForm] Preserving context:', { brandId, productId, hasProductData: !!productData, headlineMergeMode });

    const response = await apiRequest('/api/refine-idea/apply', {
      method: 'POST',
      body: JSON.stringify({
        ideaId: currentIdea.dbId || currentIdea.id,
        originalIdea: currentIdea,
        suggestions: refineState.previewData.suggestions,
        selectedHeadlineIndex: refineState.selectedHeadlineIndex,
        generateImage,
        brandId,  // Pass brandId for proper brand association
        productId,  // Pass productId for proper product association
        brief,  // Pass brief with productData for meta storage
        headlineMergeMode  // 'add' = append to existing, 'replace' = replace all
      })
    });

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Failed to apply refinement');
    }

    console.log('[RefineForm] Refinement applied:', data.idea.title);

    // Note: Toast is shown by the callback handler in idea-canvas.js

    // CREDIT-FIX: Refresh credits from server to show true balance
    refreshCreditsFromServer();

    // REFACTOR-03: Emit idea refined event for cross-module communication
    emit(IDEA_EVENTS.REFINED, {
      idea: data.idea,
      image: data.image,
      mode: refineState.currentMode
    });

    // Clear preview state
    refineState.previewData = null;
    refineState.selectedHeadlineIndex = 0;

    // Callback with refined data
    if (callbacks.onComplete) {
      callbacks.onComplete(data.idea, data.image);
    }

  } catch (error) {
    console.error('[RefineForm] Apply error:', error);
    if (window.Toast) window.Toast.error('Failed to apply: ' + error.message);
    // CREDIT-FIX: Rollback optimistic animation by refreshing true balance
    refreshCreditsFromServer();
  }
}

/**
 * Handle discarding refinement preview
 * @param {Object} callbacks - Callback functions
 */
export function handleDiscardRefinement(callbacks = {}) {
  refineState.previewData = null;
  refineState.selectedHeadlineIndex = 0;

  if (callbacks.onDiscard) {
    callbacks.onDiscard();
  }
}

/**
 * Handle headline navigation in preview
 * @param {number} direction - 1 for next, -1 for previous
 */
export function handlePreviewHeadlineNav(direction) {
  if (!refineState.previewData) return;

  const suggestions = refineState.previewData.suggestions;
  const format = refineState.format;
  const alternatives = suggestions.alternativeHeadlines || mapAlternatives(suggestions, format);
  const total = alternatives.length + 1; // +1 for main headline

  // Calculate new index
  let newIndex = refineState.selectedHeadlineIndex + direction;
  if (newIndex < 0) newIndex = total - 1;
  if (newIndex >= total) newIndex = 0;

  selectHeadlineByIndex(newIndex);
}

/**
 * Handle direct click on headline item
 * @param {number} index - The index of the clicked headline (0-based)
 */
export function handleHeadlineSelect(index) {
  if (!refineState.previewData) return;
  selectHeadlineByIndex(index);
}

/**
 * Handle headline merge mode change
 * @param {string} mode - 'add' or 'replace'
 */
export function handleHeadlineMergeModeChange(mode) {
  refineState.headlineMergeMode = mode;
  console.log('[RefineForm] Headline merge mode:', mode);
}

/**
 * Select headline by index and update DOM
 * @param {number} newIndex - The index to select (0-based)
 */
function selectHeadlineByIndex(newIndex) {
  refineState.selectedHeadlineIndex = newIndex;

  // Update DOM - find all headline items and toggle selected state
  const headlineItems = document.querySelectorAll('.refine-headline-item');
  headlineItems.forEach((item, i) => {
    item.classList.toggle('refine-headline-item--selected', i === newIndex);
  });
}

/**
 * Handle format conversion request
 * @param {Object} currentIdea - The current idea
 * @param {string} targetFormat - The target format
 * @param {Object} callbacks - Callback functions
 */
export async function handleConvertFormat(currentIdea, targetFormat, callbacks = {}) {
  if (!currentIdea || refineState.isRefining) return;

  refineState.isRefining = true;

  // CREDIT-FIX: Animate credit deduction for format conversion (using centralized pricing)
  animateCoinSpend(FORMAT_CONVERSION_COST);

  try {
    const response = await apiRequest('/api/convert-format', {
      method: 'POST',
      body: JSON.stringify({
        idea: currentIdea,
        brief: GeneratorState?.brief || {},
        fromFormat: currentIdea.format || 'print',
        toFormat: targetFormat
      })
    });

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Conversion failed');
    }

    console.log('[RefineForm] Got conversion preview:', data.newFormat);

    // Store and show preview
    refineState.previewData = { ...data, isConversion: true };

    if (callbacks.onConversionPreview) {
      callbacks.onConversionPreview(data);
    }

  } catch (error) {
    console.error('[RefineForm] Conversion error:', error);
    if (window.Toast) window.Toast.error('Failed to convert: ' + error.message);
    // CREDIT-FIX: Rollback optimistic animation by refreshing true balance
    refreshCreditsFromServer();
  } finally {
    refineState.isRefining = false;
  }
}

/**
 * Handle accepting format conversion
 * @param {Object} currentIdea - The current idea
 * @param {Object} callbacks - Callback functions
 */
export async function handleAcceptConversion(currentIdea, callbacks = {}) {
  if (!refineState.previewData?.isConversion) return;

  const generateImage = document.getElementById('convert-generate-image')?.checked ?? false;

  // CREDIT-FIX: Animate credit deduction if generating image (using centralized pricing)
  if (generateImage) {
    animateCoinSpend(AUTO_IMAGE_COST);
  }

  // Show loading state on button
  const btn = document.getElementById('convert-btn-accept');
  if (btn) {
    btn.disabled = true;
    const btnText = btn.querySelector('.btn-text');
    const btnLoading = btn.querySelector('.btn-loading');
    if (btnText) btnText.style.display = 'none';
    if (btnLoading) btnLoading.style.display = 'inline';
  }

  try {
    // Extract context from original idea to preserve in new idea
    const productId = currentIdea.productId || currentIdea.product_id ||
                      currentIdea.meta?.brief?.productId || GeneratorState?.brief?.productId || null;
    const productData = currentIdea.meta?.brief?.productData ||
                        GeneratorState?.sessionProduct ||
                        GeneratorState?.selectedProduct || null;

    const originalContext = {
      sessionId: currentIdea.session_id || currentIdea.sessionId || currentIdea.meta?.sessionId,
      brandId: currentIdea.brand_id || currentIdea.brandId || currentIdea.meta?.brandId,
      productId,  // V5: Preserve product association
      brief: {
        ...(currentIdea.meta?.brief || {}),
        ...(GeneratorState?.brief || {}),
        productId,
        productData
      }
    };

    // Inject Brand DNA into brief if available but missing (for image generation)
    // Priority: 1) Already in brief, 2) GeneratorState.currentBrand, 3) Skip
    if (!originalContext.brief.brand && GeneratorState?.currentBrand) {
      originalContext.brief.brand = {
        id: GeneratorState.currentBrand.id,
        name: GeneratorState.currentBrand.name,
        visual_prompt: GeneratorState.currentBrand.visual_prompt,
        voice_prompt: GeneratorState.currentBrand.voice_prompt
      };
      console.log('[RefineForm] Injected brand DNA into conversion brief');
    }

    console.log('[RefineForm] Conversion context:', { brandId: originalContext.brandId, productId, hasProductData: !!productData });

    const response = await apiRequest('/api/convert-format/apply', {
      method: 'POST',
      body: JSON.stringify({
        ideaId: currentIdea.dbId || currentIdea.id,
        convertedIdea: refineState.previewData.convertedIdea,
        generateImage,
        originalContext
      })
    });

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Failed to apply conversion');
    }

    console.log('[RefineForm] Conversion applied:', data.idea.format, 'New ID:', data.newIdeaId);

    if (window.Toast) window.Toast.success(`New ${data.idea.format.toUpperCase()} idea created!`);

    // CREDIT-FIX: Refresh credits from server to show true balance
    refreshCreditsFromServer();

    // Clear preview state
    refineState.previewData = null;
    refineState.selectedConvertFormat = null;

    // Callback with converted data (always createdNew = true now)
    if (callbacks.onConversionComplete) {
      callbacks.onConversionComplete(data.idea, data.image, true);
    }

  } catch (error) {
    console.error('[RefineForm] Apply conversion error:', error);
    if (window.Toast) window.Toast.error('Failed to apply: ' + error.message);
    // CREDIT-FIX: Rollback optimistic animation by refreshing true balance
    refreshCreditsFromServer();

    // Reset button state on error
    const btn = document.getElementById('convert-btn-accept');
    if (btn) {
      btn.disabled = false;
      const btnText = btn.querySelector('.btn-text');
      const btnLoading = btn.querySelector('.btn-loading');
      if (btnText) btnText.style.display = 'inline';
      if (btnLoading) btnLoading.style.display = 'none';
    }
  }
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Get headline field name for format
 */
function getHeadlineField(format) {
  const fields = {
    video: 'super',
    social: 'hook',
    digital: 'cta',
    experiential: 'reveal',
    campaign: 'campaignLine'
  };
  return fields[format] || 'headline';
}

/**
 * Get headline label for format
 */
function getHeadlineLabel(format) {
  const labels = {
    video: 'SUPER',
    social: 'HOOK',
    digital: 'CTA',
    experiential: 'REVEAL',
    campaign: 'TAGLINE'
  };
  return labels[format] || 'HEADLINE';
}

/**
 * Get visual field name for format
 * Different formats store their "visual" content in different fields
 */
function getVisualField(format) {
  const fields = {
    social: 'sequence',       // Beat-by-beat content sequence
    digital: 'frames',        // Animation/frame descriptions
    experiential: 'setup',    // Physical installation visual
    campaign: 'heroExecution' // Hero execution description
  };
  return fields[format] || 'visual';
}

/**
 * Get visual field label for format
 */
function getVisualLabel(format) {
  const labels = {
    social: 'SEQUENCE',
    digital: 'FRAMES',
    experiential: 'SETUP',
    campaign: 'HERO EXECUTION',
    video: 'VISUAL'
  };
  return labels[format] || 'VISUAL';
}

/**
 * Map format-specific alternatives to unified array
 */
function mapAlternatives(suggestions, format) {
  if (suggestions.alternativeHeadlines) return suggestions.alternativeHeadlines;

  if (format === 'video' && suggestions.alternativeSupers) {
    return suggestions.alternativeSupers.map(s => ({ headline: s.super || s }));
  }
  if (format === 'social' && suggestions.alternativeHooks) {
    return suggestions.alternativeHooks.map(h => ({ headline: h.hook || h.caption || h }));
  }
  if (format === 'digital' && suggestions.alternativeCTAs) {
    return suggestions.alternativeCTAs.map(c => ({ headline: c.cta || c }));
  }
  if (format === 'experiential' && suggestions.alternativeReveals) {
    return suggestions.alternativeReveals.map(r => ({ headline: r.reveal || r }));
  }
  if (format === 'campaign' && suggestions.alternativeLines) {
    return suggestions.alternativeLines.map(l => ({ headline: l.campaignLine || l }));
  }

  return [];
}

// ============================================================================
// FORMAT-SPECIFIC EXECUTION RENDERERS
// ============================================================================

/**
 * Render SOCIAL format execution preview
 * Fields: sequence, patternInterrupt, sound
 * FIX: Show field if EITHER original OR suggestions has it
 */
function renderSocialExecution(original, suggestions) {
  let html = '';

  // Sequence (beat-by-beat content)
  if (original.sequence || suggestions.sequence) {
    html += `
      <div class="refine-comparison">
        <div class="refine-comparison-label">SEQUENCE</div>
        <div class="refine-before">
          <span class="refine-tag">BEFORE</span>
          <span class="refine-value">${escapeHtml(original.sequence || '(not set)')}</span>
        </div>
        <div class="refine-after">
          <span class="refine-tag">AFTER</span>
          <span class="refine-value">${escapeHtml(suggestions.sequence || '(unchanged)')}</span>
        </div>
      </div>
    `;
  }

  // Pattern Interrupt
  if (original.patternInterrupt || suggestions.patternInterrupt) {
    html += `
      <div class="refine-comparison">
        <div class="refine-comparison-label">PATTERN INTERRUPT</div>
        <div class="refine-before">
          <span class="refine-tag">BEFORE</span>
          <span class="refine-value">${escapeHtml(original.patternInterrupt || '(not set)')}</span>
        </div>
        <div class="refine-after">
          <span class="refine-tag">AFTER</span>
          <span class="refine-value">${escapeHtml(suggestions.patternInterrupt || '(unchanged)')}</span>
        </div>
      </div>
    `;
  }

  // Sound/Audio
  if (original.sound || suggestions.sound) {
    html += `
      <div class="refine-comparison">
        <div class="refine-comparison-label">SOUND</div>
        <div class="refine-before">
          <span class="refine-tag">BEFORE</span>
          <span class="refine-value">${escapeHtml(original.sound || '(not set)')}</span>
        </div>
        <div class="refine-after">
          <span class="refine-tag">AFTER</span>
          <span class="refine-value">${escapeHtml(suggestions.sound || '(unchanged)')}</span>
        </div>
      </div>
    `;
  }

  return html;
}

/**
 * Render DIGITAL format execution preview
 * Fields: frames, interaction, sizes
 * FIX: Show field if EITHER original OR suggestions has it
 */
function renderDigitalExecution(original, suggestions) {
  let html = '';

  // Frames
  if (original.frames || suggestions.frames) {
    html += `
      <div class="refine-comparison">
        <div class="refine-comparison-label">FRAMES</div>
        <div class="refine-before">
          <span class="refine-tag">BEFORE</span>
          <span class="refine-value">${escapeHtml(original.frames || '(not set)')}</span>
        </div>
        <div class="refine-after">
          <span class="refine-tag">AFTER</span>
          <span class="refine-value">${escapeHtml(suggestions.frames || '(unchanged)')}</span>
        </div>
      </div>
    `;
  }

  // Interaction
  if (original.interaction || suggestions.interaction) {
    html += `
      <div class="refine-comparison">
        <div class="refine-comparison-label">INTERACTION</div>
        <div class="refine-before">
          <span class="refine-tag">BEFORE</span>
          <span class="refine-value">${escapeHtml(original.interaction || '(not set)')}</span>
        </div>
        <div class="refine-after">
          <span class="refine-tag">AFTER</span>
          <span class="refine-value">${escapeHtml(suggestions.interaction || '(unchanged)')}</span>
        </div>
      </div>
    `;
  }

  // Sizes
  if (original.sizes || suggestions.sizes) {
    html += `
      <div class="refine-comparison">
        <div class="refine-comparison-label">SIZES</div>
        <div class="refine-before">
          <span class="refine-tag">BEFORE</span>
          <span class="refine-value">${escapeHtml(original.sizes || '(not set)')}</span>
        </div>
        <div class="refine-after">
          <span class="refine-tag">AFTER</span>
          <span class="refine-value">${escapeHtml(suggestions.sizes || '(unchanged)')}</span>
        </div>
      </div>
    `;
  }

  return html;
}

/**
 * Render EXPERIENTIAL format execution preview
 * Fields: setup, participation, reveal, capture, scale, location, takeaway
 * FIX: Show field if EITHER original OR suggestions has it
 */
function renderExperientialExecution(original, suggestions) {
  let html = '';

  const fields = [
    { key: 'setup', label: 'SETUP' },
    { key: 'participation', label: 'PARTICIPATION' },
    { key: 'reveal', label: 'REVEAL' },
    { key: 'capture', label: 'CAPTURE' },
    { key: 'scale', label: 'SCALE' },
    { key: 'location', label: 'LOCATION' },
    { key: 'takeaway', label: 'TAKEAWAY' }
  ];

  fields.forEach(({ key, label }) => {
    if (original[key] || suggestions[key]) {
      html += `
        <div class="refine-comparison">
          <div class="refine-comparison-label">${label}</div>
          <div class="refine-before">
            <span class="refine-tag">BEFORE</span>
            <span class="refine-value">${escapeHtml(original[key] || '(not set)')}</span>
          </div>
          <div class="refine-after">
            <span class="refine-tag">AFTER</span>
            <span class="refine-value">${escapeHtml(suggestions[key] || '(unchanged)')}</span>
          </div>
        </div>
      `;
    }
  });

  return html;
}

/**
 * Render CAMPAIGN format execution preview
 * Fields: extensions, systemLogic, participation, earned, heroExecution
 * FIX: Show field if EITHER original OR suggestions has it
 */
function renderCampaignExecution(original, suggestions) {
  let html = '';

  const fields = [
    { key: 'heroExecution', label: 'HERO EXECUTION' },
    { key: 'extensions', label: 'EXTENSIONS' },
    { key: 'systemLogic', label: 'SYSTEM LOGIC' },
    { key: 'participation', label: 'PARTICIPATION' },
    { key: 'earned', label: 'EARNED MEDIA' }
  ];

  fields.forEach(({ key, label }) => {
    if (original[key] || suggestions[key]) {
      html += `
        <div class="refine-comparison">
          <div class="refine-comparison-label">${label}</div>
          <div class="refine-before">
            <span class="refine-tag">BEFORE</span>
            <span class="refine-value">${escapeHtml(original[key] || '(not set)')}</span>
          </div>
          <div class="refine-after">
            <span class="refine-tag">AFTER</span>
            <span class="refine-value">${escapeHtml(suggestions[key] || '(unchanged)')}</span>
          </div>
        </div>
      `;
    }
  });

  return html;
}

/**
 * Get current state
 */
export function getRefineState() {
  return { ...refineState };
}

/**
 * Reset state
 * Called when opening a new idea to ensure format-specific modes are correct
 */
export function resetRefineState() {
  refineState.isRefining = false;
  refineState.currentMode = 'full';          // Reset to default mode
  refineState.availableModes = [];           // Clear cached modes (will be fetched for new format)
  refineState.previewData = null;
  refineState.selectedHeadlineIndex = 0;
  refineState.format = 'print';              // Reset to default format
  refineState.currentIdea = null;            // Clear idea reference
  refineState.selectedConvertFormat = null;  // Clear format conversion selection
  refineState.headlineMergeMode = 'add';     // Reset merge mode
}

export function getCanvasRefineState() {
  return { isRefining: refineState.isRefining };
}

export function resetCanvasRefineState() {
  resetRefineState();

  const submitBtn = document.getElementById('canvas-btn-refine-idea');
  const btnText = submitBtn?.querySelector('.btn-text');
  const btnLoading = submitBtn?.querySelector('.btn-loading');

  if (btnText) btnText.style.display = 'inline';
  if (btnLoading) btnLoading.style.display = 'none';
  if (submitBtn) submitBtn.disabled = false;
}
